import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus, RefreshCw, UsersRound, Copy, Check, Wand2, Pencil, Trash2, KeyRound, Sparkles, Lock, LockOpen,
  UserPlus, Briefcase, Mail, Phone, Calendar as CalendarIcon, Building2, ShieldCheck,
  UserCheck, CheckCircle2, GraduationCap, CalendarClock, Send, ChevronRight, Search, Filter, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import StatusPill from "@/components/StatusPill";
import PersonAvatar from "@/components/PersonAvatar";
import PhotoUploader from "@/components/PhotoUploader";

const ROLES = [
  { value: "admin", label: "Admin", description: "Full access — user & site management", manager: true },
  { value: "super_admin", label: "Super Admin", description: "Full access · organisation-wide overrides", manager: true },
  { value: "registered_manager", label: "Registered Manager", description: "CQC-registered manager for the service", manager: true },
  { value: "service_manager", label: "Service Manager", description: "Service-level operational oversight", manager: true },
  { value: "deputy_manager", label: "Deputy Manager", description: "Deputises for the Registered Manager", manager: true },
  { value: "manager", label: "Manager", description: "Read/write across modules, cannot manage users", manager: true },
  { value: "care_worker", label: "Care Worker", description: "Day-to-day logging, no deletes", manager: false },
];

const EMPLOYMENT_TYPES = [
  { value: "full_time", label: "Full-time" },
  { value: "part_time", label: "Part-time" },
  { value: "bank", label: "Bank / Casual" },
  { value: "agency", label: "Agency" },
  { value: "volunteer", label: "Volunteer" },
];

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("") + "!";
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function CredentialsDialog({ open, onOpenChange, credentials }) {
  const [copied, setCopied] = useState({ email: false, password: false, combined: false });
  const copy = async (key, value) => {
    try { await navigator.clipboard.writeText(value); } catch (err) { console.debug("Clipboard write blocked", err); }
    setCopied((s) => ({ ...s, [key]: true }));
    setTimeout(() => setCopied((s) => ({ ...s, [key]: false })), 1500);
  };
  if (!credentials) return null;
  const combined = `Email: ${credentials.email}\nPassword: ${credentials.password}\nLogin at: ${window.location.origin}/login`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="credentials-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Login details for {credentials.name}</DialogTitle>
          <DialogDescription>Share these credentials securely — the password will not be shown again.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 mono bg-slate-100 border border-slate-200 px-2 py-1.5 text-slate-800">{credentials.email}</code>
              <button onClick={() => copy("email", credentials.email)} className="p-1.5 text-slate-500 hover:text-sky-700" data-testid="copy-email">
                {copied.email ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Temporary Password</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 mono bg-slate-100 border border-slate-200 px-2 py-1.5 text-slate-800">{credentials.password}</code>
              <button onClick={() => copy("password", credentials.password)} className="p-1.5 text-slate-500 hover:text-sky-700" data-testid="copy-password">
                {copied.password ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="text-xs text-slate-500">
            Ask the user to sign in at <a href="/login" target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">{window.location.origin}/login</a>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => copy("combined", combined)} data-testid="copy-combined">
            {copied.combined ? "Copied ✓" : "Copy all"}
          </Button>
          <Button onClick={() => onOpenChange(false)} className="bg-sky-700 hover:bg-sky-800" data-testid="credentials-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- Add Staff Wizard ------------------------------------------------------
const EMPTY_STAFF = {
  name: "", preferred_name: "", job_title: "",
  role: "care_worker", site_id: "", employment_type: "full_time",
  email: "", phone: "", start_date: todayIso(), manager_id: "",
  create_login: true, temporary_password: "", send_welcome_email: true,
};

function AddStaffWizard({ open, onOpenChange, sites, managers, onCreated }) {
  const [form, setForm] = useState(EMPTY_STAFF);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { user, temporary_password, welcome_sent }
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_STAFF, temporary_password: genPassword() });
      setResult(null);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Full Name is required");
    if (!form.email.trim()) return toast.error("Work Email is required");
    if (!form.job_title.trim()) return toast.error("Job Title is required");
    if (form.create_login && (!form.temporary_password || form.temporary_password.length < 8)) {
      return toast.error("Temporary password must be at least 8 characters");
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        preferred_name: form.preferred_name.trim() || null,
        job_title: form.job_title.trim() || null,
        role: form.role,
        site_id: form.site_id || null,
        employment_type: form.employment_type || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        start_date: form.start_date || null,
        manager_id: form.manager_id || null,
        create_login: !!form.create_login,
        temporary_password: form.create_login ? form.temporary_password : null,
        send_welcome_email: !!form.create_login && !!form.send_welcome_email,
      };
      const r = await api.post("/auth/onboard-staff", payload);
      setResult(r.data);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="add-staff-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                  <UserPlus className="w-5 h-5 text-sky-700" />
                </div>
                <div>
                  <DialogTitle className="font-heading text-lg">Add Staff Member</DialogTitle>
                  <DialogDescription>
                    Add a new staff member to AYOBELL. A secure login account will be created automatically.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5 py-2 max-h-[65vh] overflow-y-auto pr-1">
              {/* Personal */}
              <Section icon={UserCheck} title="Personal details">
                <Row>
                  <Field label="Full Name *" testid="staff-name">
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Sarah Johnson" data-testid="staff-name" required />
                  </Field>
                  <Field label="Preferred Name" testid="staff-preferred-name">
                    <Input value={form.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} placeholder="Sarah" data-testid="staff-preferred-name" />
                  </Field>
                </Row>
              </Section>

              {/* Role & Employment */}
              <Section icon={Briefcase} title="Role & employment">
                <Row>
                  <Field label="Job Title *" testid="staff-job-title">
                    <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} placeholder="Senior Care Assistant" data-testid="staff-job-title" required />
                  </Field>
                  <Field label="Employment Type" testid="staff-employment-type">
                    <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                      <SelectTrigger data-testid="staff-employment-type" className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {EMPLOYMENT_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value} data-testid={`staff-employment-${t.value}`}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </Row>
                <Row>
                  <Field label="Role *" testid="staff-role" hint="Determines system permissions">
                    <Select value={form.role} onValueChange={(v) => set("role", v)}>
                      <SelectTrigger data-testid="staff-role" className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {ROLES.map((r) => (
                          <SelectItem key={r.value} value={r.value} data-testid={`staff-role-${r.value}`}>
                            <div className="flex flex-col items-start">
                              <span>{r.label}</span>
                              <span className="text-[11px] text-slate-500">{r.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Site" testid="staff-site">
                    <Select value={form.site_id || "__all__"} onValueChange={(v) => set("site_id", v === "__all__" ? "" : v)}>
                      <SelectTrigger data-testid="staff-site" className="bg-white"><SelectValue placeholder="All sites" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="__all__" data-testid="staff-site-all">All sites (no restriction)</SelectItem>
                        {sites.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`staff-site-${s.id}`}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </Row>
                <Row>
                  <Field label="Start Date" testid="staff-start-date" icon={CalendarIcon}>
                    <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="staff-start-date" />
                  </Field>
                  <Field label="Line Manager" testid="staff-manager" hint="Who this person reports to">
                    <Select value={form.manager_id || "__none__"} onValueChange={(v) => set("manager_id", v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="staff-manager" className="bg-white"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="__none__" data-testid="staff-manager-none">Unassigned</SelectItem>
                        {managers.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`staff-manager-${m.id}`}>{m.name} <span className="text-slate-400">· {m.role}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </Row>
              </Section>

              {/* Contact */}
              <Section icon={Mail} title="Contact">
                <Row>
                  <Field label="Work Email *" testid="staff-email" icon={Mail}>
                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="sarah.johnson@ayobell.com" data-testid="staff-email" required />
                  </Field>
                  <Field label="Phone Number" testid="staff-phone" icon={Phone}>
                    <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+44 7700 900000" data-testid="staff-phone" />
                  </Field>
                </Row>
              </Section>

              {/* Login */}
              <Section icon={ShieldCheck} title="Login access">
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
                  <div>
                    <div className="text-sm font-medium text-slate-800">Create Login Account</div>
                    <div className="text-[12px] text-slate-500">Turn this off for staff who won&apos;t use the portal (e.g. some agency workers).</div>
                  </div>
                  <Switch checked={form.create_login} onCheckedChange={(v) => set("create_login", v)} data-testid="staff-create-login" />
                </div>

                {form.create_login && (
                  <>
                    <Field label="Temporary Password" testid="staff-temp-password" hint="User will be prompted to change it after first login">
                      <div className="flex gap-2">
                        <Input value={form.temporary_password} onChange={(e) => set("temporary_password", e.target.value)} className="mono text-xs" data-testid="staff-temp-password" />
                        <Button type="button" variant="outline" size="sm" onClick={() => set("temporary_password", genPassword())} className="shrink-0" data-testid="staff-gen-password">
                          <Wand2 className="w-3.5 h-3.5 mr-1" /> Regenerate
                        </Button>
                      </div>
                    </Field>

                    <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
                      <div>
                        <div className="text-sm font-medium text-slate-800">Send Welcome Email</div>
                        <div className="text-[12px] text-slate-500">Emails a branded welcome message with sign-in instructions.</div>
                      </div>
                      <Switch checked={form.send_welcome_email} onCheckedChange={(v) => set("send_welcome_email", v)} data-testid="staff-send-welcome" />
                    </div>
                  </>
                )}
              </Section>

              {/* Auto actions */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900" data-testid="staff-auto-actions">
                <div className="font-semibold mb-1 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Ayobell will automatically:</div>
                <ul className="ml-5 list-disc space-y-0.5 marker:text-emerald-500">
                  <li>Create the staff record</li>
                  {form.create_login && <li>Create a secure login account</li>}
                  <li>Assign permissions from the <b>{ROLES.find((r) => r.value === form.role)?.label}</b> role</li>
                  <li>Assign to <b>{form.site_id ? (sites.find((s) => s.id === form.site_id)?.name || "the selected site") : "all sites"}</b></li>
                  <li>Make them available for rota assignment</li>
                  {form.create_login && form.send_welcome_email && <li>Send a welcome email with sign-in details</li>}
                </ul>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="staff-cancel">Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="staff-submit">
                  {saving ? "Creating…" : "Create Staff Member"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <SuccessScreen result={result} onOpenChange={onOpenChange} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest font-semibold text-slate-500">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function SuccessScreen({ result, onOpenChange }) {
  const navigate = useNavigate();
  const { user, temporary_password: tempPw, welcome_sent } = result;
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(welcome_sent);

  const copyPw = async () => {
    if (!tempPw) return;
    try { await navigator.clipboard.writeText(tempPw); } catch (err) { console.debug(err); }
    setCopied(true);
    toast.success("Password copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const resendWelcome = async () => {
    setSending(true);
    try {
      const r = await api.post(`/auth/users/${user.id}/send-welcome`);
      if (r.data?.sent) { setSent(true); toast.success("Welcome email sent"); }
      else toast.error("Email service unavailable");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const actions = [
    { key: "profile", label: "Open Staff Profile", icon: UserCheck, onClick: () => { onOpenChange(false); navigate(`/users`); } },
    { key: "training", label: "Assign Training", icon: GraduationCap, onClick: () => { onOpenChange(false); navigate(`/training`); } },
    { key: "shift", label: "Create First Shift", icon: CalendarClock, onClick: () => { onOpenChange(false); navigate(`/staff-schedule`); } },
    { key: "welcome", label: sent ? "Welcome Email Sent ✓" : "Send Welcome Email", icon: Send, disabled: sending || sent, onClick: resendWelcome },
    ...(tempPw ? [{ key: "copy", label: copied ? "Password Copied ✓" : "Copy Temporary Password", icon: copied ? Check : Copy, onClick: copyPw }] : []),
  ];

  return (
    <div data-testid="staff-success">
      <div className="flex flex-col items-center text-center py-4">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
        </div>
        <h2 className="font-heading text-xl font-semibold text-slate-900">Staff Member Created Successfully</h2>
        <div className="mt-1 text-sm text-slate-600 max-w-md">
          <span className="font-medium text-slate-800">{user.name}</span>
          {user.job_title && <> · <span className="text-slate-500">{user.job_title}</span></>}
          <br />
          <span className="text-slate-500">{user.email}</span>
        </div>
        {welcome_sent && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="staff-welcome-badge">
            <Check className="w-3 h-3" /> Welcome email sent to {user.email}
          </div>
        )}
      </div>

      {tempPw && (
        <div className="mt-2 mb-4 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="staff-credentials-block">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Temporary Password</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 mono bg-white border border-slate-200 px-2.5 py-1.5 text-sm text-slate-900 rounded">{tempPw}</code>
            <Button type="button" size="sm" variant="outline" onClick={copyPw} data-testid="btn-copy-temp-password">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">This password won&apos;t be shown again — copy it now or resend the welcome email.</div>
        </div>
      )}

      <div className="mt-2">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Quick Actions</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.key}
                type="button"
                onClick={a.onClick}
                disabled={a.disabled}
                data-testid={`staff-action-${a.key}`}
                className="group flex items-center gap-2.5 border border-slate-200 hover:border-sky-400 hover:bg-sky-50 disabled:opacity-60 disabled:hover:bg-white disabled:hover:border-slate-200 rounded-md px-3 py-2.5 text-left transition-colors"
              >
                <Icon className="w-4 h-4 text-slate-500 group-hover:text-sky-700 shrink-0" />
                <span className="text-sm font-medium text-slate-800 group-hover:text-sky-900 flex-1">{a.label}</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-sky-700 shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      <DialogFooter className="mt-6">
        <Button onClick={() => onOpenChange(false)} className="bg-sky-700 hover:bg-sky-800" data-testid="staff-success-done">Done</Button>
      </DialogFooter>
    </div>
  );
}

// -- Main Users page --------------------------------------------------------
export default function Users() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "care_worker", site_id: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPasswordVal] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [credOpen, setCredOpen] = useState(false);
  // Directory filters
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const isAdmin = currentUser?.role === "admin";

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/auth/users"), api.get("/sites")])
      .then(([u, s]) => { setUsers(u.data || []); setSites(s.data || []); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const managers = useMemo(
    () => users.filter((u) => ["admin", "super_admin", "registered_manager", "service_manager", "deputy_manager", "manager"].includes(u.role)).map((u) => ({ id: u.id, name: u.name || u.email, role: u.role })),
    [users],
  );

  // Directory filtering — status is derived from `login_enabled` (true = Active, false = Disabled).
  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (siteFilter === "__all_sites__") { if (u.site_id) return false; }
      else if (siteFilter !== "all" && u.site_id !== siteFilter) return false;
      const status = u.login_enabled === false ? "disabled" : "active";
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (needle) {
        const hay = [u.name, u.preferred_name, u.email, u.job_title, u.phone].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [users, q, roleFilter, siteFilter, statusFilter]);

  const filtersActive = q || roleFilter !== "all" || siteFilter !== "all" || statusFilter !== "all";
  const resetFilters = () => { setQ(""); setRoleFilter("all"); setSiteFilter("all"); setStatusFilter("all"); };

  const openEdit = (u) => {
    setEditingId(u.id);
    setEditForm({ name: u.name || "", email: u.email || "", role: u.role, site_id: u.site_id || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error("Name required"); return; }
    setEditSaving(true);
    try {
      await api.put(`/auth/users/${editingId}`, {
        name: editForm.name.trim(),
        role: editForm.role,
        site_id: editForm.site_id || null,
      });
      toast.success("User updated");
      setEditingId(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEditSaving(false); }
  };

  const doDelete = async (u) => {
    if (u.id === currentUser?.id) { toast.error("You cannot delete your own account"); return; }
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(`Deleted ${u.name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleLock = async (u) => {
    if (u.id === currentUser?.id) { toast.error("You cannot lock your own account"); return; }
    const isLocked = u.login_enabled === false;
    if (isLocked) {
      if (!window.confirm(`Unlock ${u.name}?\nThey will be able to sign in again immediately.`)) return;
      try {
        await api.post(`/auth/users/${u.id}/unlock`);
        toast.success(`Unlocked ${u.name}`);
        load();
      } catch (err) { toast.error(formatApiError(err)); }
    } else {
      const reason = window.prompt(
        `Lock ${u.name}'s account?\n\nThis blocks future sign-ins and immediately ends any active session.\n\nReason (shown on their next login attempt):`,
        "Left the company",
      );
      if (reason === null) return;
      try {
        await api.post(`/auth/users/${u.id}/lock`, { reason });
        toast.success(`${u.name} locked out`);
        load();
      } catch (err) { toast.error(formatApiError(err)); }
    }
  };

  const openReset = (u) => {
    setResetTarget(u);
    setResetPasswordVal(genPassword());
  };

  const doReset = async () => {
    if (!resetTarget) return;
    if (resetPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/auth/users/${resetTarget.id}/reset-password`, { password: resetPassword });
      setCredentials({ name: resetTarget.name, email: resetTarget.email, password: resetPassword });
      setCredOpen(true);
      setResetTarget(null);
      setResetPasswordVal("");
      toast.success(`Password reset for ${resetTarget.name}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name || <span className="text-slate-300">—</span>;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">System</div>
          <h1 className="font-heading text-base font-semibold flex items-center gap-2" data-testid="page-title">
            <UsersRound className="w-4 h-4" /> Staff & Users
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-9 text-slate-700" data-testid="btn-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {isAdmin && (
            <>
              <Button
                size="sm" variant="outline"
                onClick={async () => {
                  if (!window.confirm("Import the demo service users John Smith (SCP-0001) and Bayo Adebola (SCM-SL-0002)? Existing records with the same reference will be updated.")) return;
                  const t = toast.loading("Seeding demo users…");
                  try {
                    const r = await api.post("/admin/seed-demo-users");
                    const present = (r.data.present_in_db || []).length;
                    const ok = (r.data.seeded || []).filter((s) => s.ok).length;
                    if (present > 0) toast.success(`Demo users ready — ${present} in DB, ${ok} freshly synced`, { id: t });
                    else toast.warning("Seed ran but no demo users found — check backend logs", { id: t });
                  } catch (e) { toast.error(formatApiError(e), { id: t }); }
                }}
                data-testid="btn-import-demo"
                className="h-9"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-violet-600" /> Import demo users
              </Button>
              <Button size="sm" onClick={() => setWizardOpen(true)} className="h-9 bg-sky-700 hover:bg-sky-800" data-testid="btn-add-staff">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Staff Member
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Mobile header — Batch 7D */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">System</div>
          <h1 className="font-heading text-base font-semibold flex items-center gap-1.5 truncate" data-testid="page-title-mobile">
            <UsersRound className="w-4 h-4 shrink-0" /> Staff & Users
          </h1>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="outline" size="sm" onClick={load} className="h-11 w-11 p-0" data-testid="btn-refresh-mobile" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => setWizardOpen(true)} className="h-11 bg-sky-700 hover:bg-sky-800" data-testid="btn-add-staff-mobile">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        {loading ? (
          <div className="p-8 text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            {/* Directory filters */}
            <div className="border-b border-slate-200 bg-slate-50 px-6 py-3 flex items-center gap-2 flex-wrap" data-testid="user-filters-bar">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, job title, phone…"
                  className="pl-8 h-9 bg-white text-sm"
                  data-testid="user-search"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="h-9 w-40 bg-white text-sm" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all" data-testid="filter-role-all">All roles</SelectItem>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`filter-role-${r.value}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="h-9 w-44 bg-white text-sm" data-testid="filter-site"><SelectValue placeholder="Site" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all" data-testid="filter-site-all">All sites</SelectItem>
                  <SelectItem value="__all_sites__" data-testid="filter-site-none">No site (org-wide)</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`filter-site-${s.id}`}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-36 bg-white text-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all" data-testid="filter-status-all">All statuses</SelectItem>
                  <SelectItem value="active" data-testid="filter-status-active">Active</SelectItem>
                  <SelectItem value="disabled" data-testid="filter-status-disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9 text-slate-600" data-testid="filter-reset">
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
              <div className="ml-auto text-xs text-slate-500 tabular-nums" data-testid="filter-count">
                <span className="font-semibold text-slate-800">{filteredUsers.length}</span> of {users.length}
              </div>
            </div>
            <table className="data-table hidden lg:table" data-testid="users-table">
            <thead>
              <tr>
                <th className="row-num">#</th>
                <th className="w-12"></th>
                <th>Name</th>
                <th>Job Title</th>
                <th>Email</th>
                <th>Role</th>
                <th>Site</th>
                <th>Start Date</th>
                {isAdmin && <th className="w-32 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u, i) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="row-num">{i + 1}</td>
                  <td><PersonAvatar person={u} size="sm" testid={`avatar-user-${u.id}`} /></td>
                  <td className="font-medium">
                    <button
                      type="button"
                      onClick={() => navigate(`/staff/${u.id}`)}
                      className="text-left hover:text-sky-700 hover:underline"
                      data-testid={`open-staff-${u.id}`}
                    >
                      {u.preferred_name || u.name}
                    </button>
                    {u.preferred_name && u.name !== u.preferred_name && <span className="ml-1 text-[11px] text-slate-400">({u.name})</span>}
                    {u.id === currentUser?.id && <span className="ml-2 text-[10px] uppercase tracking-widest text-sky-700">You</span>}
                  </td>
                  <td className="text-slate-600 text-xs">{u.job_title || <span className="text-slate-300">—</span>}</td>
                  <td className="mono text-xs">{u.email}</td>
                  <td><StatusPill value={u.role} kind="generic" /></td>
                  <td>{u.site_id ? siteName(u.site_id) : <span className="text-slate-400 text-xs">All sites</span>}</td>
                  <td className="text-xs text-slate-500">{u.start_date || u.created_at?.slice(0, 10)}</td>
                  {isAdmin && (
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => openEdit(u)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`edit-${u.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => openReset(u)} className="p-1 text-slate-500 hover:text-amber-700 ml-0.5" data-testid={`reset-${u.id}`} title="Reset password"><KeyRound className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => toggleLock(u)}
                        disabled={u.id === currentUser?.id}
                        className={`p-1 ml-0.5 disabled:opacity-30 disabled:cursor-not-allowed ${u.login_enabled === false ? "text-emerald-600 hover:text-emerald-800" : "text-slate-500 hover:text-red-700"}`}
                        data-testid={`lock-${u.id}`}
                        title={u.id === currentUser?.id ? "You cannot lock yourself" : (u.login_enabled === false ? "Unlock account" : "Lock account (staff has left)")}
                      >{u.login_enabled === false ? <LockOpen className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}</button>
                      <button
                        onClick={() => doDelete(u)}
                        disabled={u.id === currentUser?.id}
                        className="p-1 text-slate-500 hover:text-red-600 ml-0.5 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`delete-${u.id}`} title={u.id === currentUser?.id ? "Cannot delete yourself" : "Delete"}
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  )}
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 9 : 8} className="p-6 text-center text-sm text-slate-500" data-testid="users-empty">
                    <Filter className="w-5 h-5 mx-auto text-slate-300 mb-1.5" />
                    {filtersActive ? "No staff match these filters. " : "No staff yet. "}
                    {filtersActive ? <button onClick={resetFilters} className="text-sky-700 hover:underline">Clear filters</button> : null}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Mobile card list — Batch 7D */}
          <ul className="lg:hidden divide-y divide-slate-200 border-t border-slate-200" data-testid="users-cards">
            {filteredUsers.length === 0 && (
              <li className="p-6 text-center text-sm text-slate-500" data-testid="users-empty-mobile">
                <Filter className="w-5 h-5 mx-auto text-slate-300 mb-1.5" />
                {filtersActive ? "No staff match these filters. " : "No staff yet. "}
                {filtersActive ? <button onClick={resetFilters} className="text-sky-700 hover:underline">Clear filters</button> : null}
              </li>
            )}
            {filteredUsers.map((u) => (
              <li key={u.id} className="p-3 flex items-start gap-3 bg-white" data-testid={`user-card-${u.id}`}>
                <PersonAvatar person={u} size="sm" testid={`avatar-user-card-${u.id}`} />
                <div className="flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/staff/${u.id}`)}
                    className="text-left font-medium text-slate-900 hover:text-sky-700 break-words"
                    data-testid={`open-staff-card-${u.id}`}
                  >
                    {u.preferred_name || u.name}
                  </button>
                  {u.id === currentUser?.id && <span className="ml-2 text-[10px] uppercase tracking-widest text-sky-700">You</span>}
                  <div className="text-[11px] text-slate-600 break-all">{u.email}</div>
                  {u.job_title && <div className="text-[11px] text-slate-500 break-words">{u.job_title}</div>}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <StatusPill value={u.role} kind="generic" />
                    <span className="text-[10px] text-slate-500">
                      {u.site_id ? (sites.find((s) => s.id === u.site_id)?.name || "—") : "All sites"}
                    </span>
                    {u.login_enabled === false && (
                      <span className="text-[10px] uppercase tracking-widest text-red-700 border border-red-300 bg-red-50 px-1.5 py-0.5 rounded">Locked</span>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <button onClick={() => openEdit(u)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 hover:text-sky-700" data-testid={`edit-card-${u.id}`} aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => openReset(u)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 hover:text-amber-700" data-testid={`reset-card-${u.id}`} aria-label="Reset password"><KeyRound className="w-4 h-4" /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          </>
        )}
      </div>

      {/* Add Staff wizard */}
      <AddStaffWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        sites={sites}
        managers={managers}
        onCreated={load}
      />

      {/* Edit dialog */}
      <Dialog open={!!editingId} onOpenChange={(v) => { if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-lg bg-white" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit staff member</DialogTitle>
            <DialogDescription>Update this staff member&apos;s name, role or site. Use the key icon to reset their password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3 py-2">
            <PhotoUploader
              kind="staff"
              targetId={editingId}
              person={users.find((u) => u.id === editingId) || {}}
              onUpdated={() => load()}
            />
            <div>
              <Label className="text-xs font-medium">Full Name *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 h-9" data-testid="edit-user-name" required />
            </div>
            <div>
              <Label className="text-xs font-medium">Email</Label>
              <Input value={editForm.email} readOnly disabled className="mt-1 h-9 mono text-xs bg-slate-50" data-testid="edit-user-email" />
            </div>
            <div>
              <Label className="text-xs font-medium">Role *</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger className="mt-1 h-9 bg-white" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`edit-user-role-${r.value}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Site (optional)</Label>
              <Select value={editForm.site_id || "__all__"} onValueChange={(v) => setEditForm({ ...editForm, site_id: v === "__all__" ? "" : v })}>
                <SelectTrigger className="mt-1 h-9 bg-white" data-testid="edit-user-site"><SelectValue placeholder="All sites" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__all__">All sites (no restriction)</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingId(null)} data-testid="edit-user-cancel">Cancel</Button>
              <Button type="submit" disabled={editSaving} className="bg-sky-700 hover:bg-sky-800" data-testid="edit-user-save">
                {editSaving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetPasswordVal(""); } }}>
        <DialogContent className="max-w-md bg-white" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset password{resetTarget ? ` — ${resetTarget.name}` : ""}</DialogTitle>
            <DialogDescription>Set a new temporary password. Share it securely — the user should change it after signing in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium">New Password *</Label>
              <div className="flex gap-2 mt-1">
                <Input value={resetPassword} onChange={(e) => setResetPasswordVal(e.target.value)} className="h-9 mono text-xs" data-testid="reset-password-input" required />
                <Button type="button" variant="outline" size="sm" onClick={() => setResetPasswordVal(genPassword())} className="h-9 shrink-0" data-testid="reset-gen">
                  <Wand2 className="w-3.5 h-3.5 mr-1" /> Generate
                </Button>
              </div>
              <div className="text-xs text-slate-500 mt-1">Minimum 6 characters.</div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setResetTarget(null); setResetPasswordVal(""); }} data-testid="reset-cancel">Cancel</Button>
            <Button type="button" onClick={doReset} className="bg-amber-600 hover:bg-amber-700" data-testid="reset-confirm">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CredentialsDialog open={credOpen} onOpenChange={setCredOpen} credentials={credentials} />
    </div>
  );
}
